import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { errorText } from "../api/client";

/**
 * Camaroo login.
 * Visual structure recovered from the current live auth-login.php.
 * Authentication behaviour remains handled by AuthContext/FastAPI.
 */
export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();

  const [username, setUsername] = useState("");
  const [userpassword, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const r = await login(username, userpassword);

      if (r.Error) {
        setError(r.Error);
        return;
      }

      const v = r.Validity;

      if (v?.Status === "EXPIRED") {
        nav("/includes/Validate");
      } else if (v?.Status === "WARNING") {
        nav(`/ValidityWarning?Q=${btoa(String(v.DaysLeft))}`);
      } else {
        nav("/index");
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cam-login-page" data-testid="login-page">
      <div className="cam-login-background-image" />
      <div className="cam-login-overlay" />

      <div className="cam-login-container">
        <div className="cam-login-card">
          <div className="cam-login-logo-wrapper">
            <div className="cam-login-logo">
              <img src="/brand/camaroo-logo.png" alt="Camaroo ERP" />
            </div>
          </div>

          <div className="cam-login-welcome">
            <h1>Welcome Back</h1>
            <p>Enter your credentials to access your account</p>
          </div>

          <form onSubmit={submit} className="cam-login-form" data-testid="login-form">
            <div className="cam-login-form-group username">
              <label htmlFor="username">User Name</label>

              <div className="cam-login-input-wrapper">
                <div className="cam-login-input-icon" aria-hidden="true">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c1.5-4 5-6 8-6s6.5 2 8 6" />
                  </svg>
                </div>

                <input
                  required
                  type="text"
                  id="username"
                  name="username"
                  placeholder="Enter username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="login-username-input"
                />
              </div>
            </div>

            <div className="cam-login-form-group password">
              <label htmlFor="userpassword">Password</label>

              <div className="cam-login-input-wrapper">
                <div className="cam-login-input-icon" aria-hidden="true">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>

                <input
                  required
                  type={show ? "text" : "password"}
                  id="userpassword"
                  name="userpassword"
                  placeholder="Enter password"
                  autoComplete="current-password"
                  value={userpassword}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                />

                <button
                  type="button"
                  className="cam-login-password-toggle"
                  onClick={() => setShow((current) => !current)}
                  aria-label={show ? "Hide password" : "Show password"}
                  data-testid="login-toggle-password"
                >
                  {show ? (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                      <line x1="2" x2="22" y1="2" y2="22" />
                    </svg>
                  ) : (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="alert alert-danger erp-alert-inline cam-login-error"
                role="alert"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              className="cam-login-submit"
              disabled={busy}
              data-testid="login-submit-button"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
